import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpClientService } from '../connections/http-client.service';
import { AppSettings, ErrorCodes, VehicleInfo } from '../util/utils';
import { ImageUploadService } from './image-upload.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { AlertService } from '../util/alert.service';
import { Router, Route } from '@angular/router';
import { CarService, Pricing } from './car.service';
import { NavController, LoadingController } from '@ionic/angular';
import { Progress } from 'src/app/components/progress-bar/progress-bar.component';
import { AuthService } from 'src/app/shared/services/auth.service';

declare let fbq: Function;

const normalRoutes: string[] = ['vin-info', 'basic-info', 'car-condition', 'condition-report', 'images', 'post-form'];
const valueRoutes: string[] = [
    'vin-info',
    'basic-info',
    'car-condition',
    'show-car-value',
    'condition-report',
    'images',
    'post-form'
];

@Injectable({
    providedIn: 'root'
})
export class FormService {
    private car: BehaviorSubject<any> = new BehaviorSubject<any>({});
    private requiredImages: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(undefined);
    private pictures: BehaviorSubject<Map<string, PictureForm>> = new BehaviorSubject<Map<string, PictureForm>>(
        new Map<string, PictureForm>()
    );
    private pricing: BehaviorSubject<Pricing> = new BehaviorSubject<Pricing>(undefined);

    private progress: BehaviorSubject<Progress> = new BehaviorSubject<Progress>(undefined);

    public isContinuedForm = false;

    public conditionReportAnswered = false;

    public formDone: boolean = false;

    public editMode: boolean = false;
    public editPicturesMode: boolean = false;

    constructor(
        private http: HttpClientService,
        private imageUploadService: ImageUploadService,
        private analytics: AnalyticsService,
        private alert: AlertService,
        private router: Router,
        private carService: CarService,
        private navController: NavController,
        private auth: AuthService,
        private loadingController: LoadingController
    ) {}

    resetForm(abandon: boolean) {
        this.imageUploadService.destroyService();
        if (abandon) {
            this.abandonForm();
        }
        this.car.next({});
        this.pictures.next(new Map<string, PictureForm>());
        this.pricing.next(undefined);
        this.conditionReportAnswered = false;
        this.isContinuedForm = false;
        this.formDone = false;
        this.editMode = false;
        this.editPicturesMode = false;
    }

    abandonForm() {
        if (!this.isContinuedForm && !this.formDone) {
            this.analytics.sendAbandonedEvent(this.car);
        }
    }

    get hasVin() {
        return this.car.value.vin ? true : false;
    }

    get isEmpty() {
        return Object.keys(this.car.value).length === 0;
    }

    appendPicture(position: string, picture: string) {
        const pics = this.pictures.value;
        const posPic = pics.get(position);
        if (posPic) {
            posPic.image = picture;
            pics.set(position, posPic);
            this.pictures.next(pics);
            this.imageUploadService.addToUploadQueue(position);
        }
    }

    get isUploadingImages(): boolean {
        return this.imageUploadService.uploading;
    }

    getCar() {
        return this.car.value;
    }

    getCarState(): Observable<any> {
        return this.car.asObservable();
    }

    getPictures(): Observable<Map<string, PictureForm>> {
        return this.pictures.asObservable();
    }

    async postForm(): Promise<any> {
        const car = this.car.value;

        if (car.uid) {
            await this.updateCarPricing();
            return this.http.post('update_vehicle', this.car.value);
        } else {
            this.addNeceseryFields();
            return new Promise((resolve, reject) => {
                return this.http
                    .post(
                        'create_vehicle',
                        car,
                        (vehicle) => {
                            return vehicle;
                        },
                        { showDefaultAlert: false, errorCode: ErrorCodes.DuplicateVehicleListing }
                    )
                    .then((vehicle) => {
                        this.updateValues(vehicle);
                        resolve();
                    })
                    .catch((err) => {
                        if (err && err.code === ErrorCodes.DuplicateVehicleListing) {
                            this.alert
                                .showConfirmation(
                                    'utilsMessages.carWithVinExists.title',
                                    'utilsMessages.carWithVinExists.text',
                                    'utilsMessages.carWithVinExists.okButton',
                                    'utilsMessages.carWithVinExists.cancelButton'
                                )
                                .then((result) => {
                                    if (result) {
                                        this.router.navigateByUrl('/your-listings', { replaceUrl: true });
                                    }
                                });
                        }
                        reject();
                    })
                    .finally(() => {
                        fbq('trackCustom', 'ListingStarted', {});
                    });
            });
        }
    }

    private addNeceseryFields() {
        this.updateValues({
            odometer_units: 'miles'
        });
    }

    async listCarOnAuction(settings: AuctionSettings, agreeChecked: boolean): Promise<any> {
        this.analytics.logEvent('listing_published', this.car);
        this.analytics.logEvent('listing_agree_checked', { checked: agreeChecked });

        return this.http.post(
            'create_auction',
            {
                item_uid: this.car.value.uid,
                created: new Date(),
                start: settings.start,
                end: settings.end,
                duration: Number(settings.length),
                currency: 'USD',
                reserve: settings.reserve || 0,
                distance: settings.distance,
                buy_now: settings.buy_now,
                buy_now_price: settings.buy_now_price
            },
            (response) => {
                fbq('trackCustom', 'AuctionCreated', {});
                this.formDone = true;
                return response;
            }
        );
    }

    async getVehicleReport(): Promise<any> {
        return this.http.post(
            'vehicle_report',
            {
                uid: AppSettings.conditionReportId,
                vehicle_uid: this.car.value.uid
            },
            (response) => {
                if (response.answers) {
                    this.conditionReportAnswered = response.questions.length === response.answers.length;
                }
                return response;
            }
        );
    }

    async postConditionReport(answers: {}): Promise<any> {
        const vehicleUID = this.car.value.uid;
        const answersInArray = [];

        this.parseAnswers(answers, answersInArray);

        const conditionReportResponse = await this.http.post('submit_report', {
            uid: AppSettings.conditionReportId,
            vehicle_uid: vehicleUID,
            valid: true,
            name: 'Vehicle Report',
            answers: answersInArray
        });

        if (conditionReportResponse.answers) {
            this.conditionReportAnswered =
                conditionReportResponse.questions.length === conditionReportResponse.answers.length;
        }

        await this.initPictures(vehicleUID);

        return conditionReportResponse;
    }

    private parseAnswers(answers: {}, answersInArray: any[]) {
        for (const question in answers) {
            if (question && !question.includes('-Desc')) {
                if (answers[question + '-Desc']) {
                    answersInArray.push({
                        question_uid: question,
                        response: answers[question],
                        description: answers[question + '-Desc']
                    });
                } else {
                    answersInArray.push({
                        question_uid: question,
                        response: answers[question]
                    });
                }
            }
        }
    }

    public getCarFromVIN(vin: string): Promise<any> {
        this.updateValues({ vin: vin.toUpperCase() });
        return this.http.post(
            'vin_info',
            { vin: this.car.value.vin },
            (data) => {
                return data;
            },
            { showDefaultAlert: false }
        );
    }

    public getBussinessDays(): Promise<string[]> {
        return this.http.post('business_days', {}, (data) => data.Dates);
    }

    public async continueForm(vehicleUID: string) {
        try {
            this.isContinuedForm = true;
            this.updateValues({ uid: vehicleUID });
            this.car.next(await this.carService.getUserCar(vehicleUID));
            await this.getVehicleReport();
            this.analytics.logEvent('listing_relisted', this.car.value.uid);
        } catch {
            this.navController.pop();
            this.resetForm(false);
        }

        try {
            if (this.conditionReportAnswered) {
                await this.initPictures(vehicleUID);
            }
        } catch {}

        try {
            await this.updateCarPricing();
        } catch {}
    }

    private async initPictures(vehicleUID: string) {
        const requiredImages = await this.getRequiredImages();
        const pictures = await this.carService.getAllImages(vehicleUID);

        this.requiredImages.next(requiredImages);
        this.pictures.next(new Map<string, PictureForm>());

        this.appendTakenPictures(pictures, requiredImages);

        this.imageUploadService.destroyService();
        this.imageUploadService.initService(this.pictures, vehicleUID);
    }

    updateProgress() {
        const size = this.router.url.includes('value') ? 7 : 6;
        const current = this.getCurrentProgress();
        this.progress.next({ size, current });
    }

    private getCurrentProgress() {
        if (this.router.url.includes('images')) {
            return normalRoutes.indexOf('images');
        } else {
            return normalRoutes.indexOf(this.router.url.split('/').pop());
        }
    }

    public getProgress() {
        return this.progress.asObservable();
    }

    public async updateCarPricing() {
        try {
            const pricing: Pricing = await this.carService.getCarPricing(this.car.value);
            if (pricing.groovy > 0) {
                pricing.min = Math.round(pricing.groovy * 0.5);
                pricing.min -= pricing.min % 100;
                pricing.min += pricing.groovy % 10;
                pricing.middle = Math.round(pricing.groovy * 0.75);
                pricing.max = Math.round(pricing.groovy);
                this.pricing.next(pricing);
            }
        } catch {
            this.alert.showToast('utilsMessages.error.failedValueEstimate', 3500);
        }
    }

    get pricingInfo(): Observable<Pricing> {
        if (!this.pricing.value) {
            this.updateCarPricing();
        }
        return this.pricing.asObservable();
    }

    private appendTakenPictures(pictures: any, requiredImages: string[]) {
        const picturesMap = this.pictures.value;

        requiredImages.forEach((position) => {
            if (pictures[position]) {
                picturesMap.set(position, {
                    image: pictures[position].small,
                    uploaded: true,
                    error: false,
                    taken: true
                });
            } else {
                const image = VehicleInfo.pictureOrder.includes(position)
                    ? 'assets/car-form/' + position + '.png'
                    : 'assets/car-form/right-full.png';
                picturesMap.set(position, {
                    image,
                    uploaded: false,
                    taken: false,
                    error: false
                });
            }
        });
        this.pictures.next(picturesMap);
    }

    updateValues(newValues: any) {
        const currentValues = this.car.value;
        Object.assign(currentValues, newValues);
        this.car.next(currentValues);
    }

    private getRequiredImages() {
        return this.http.post(
            'required_photos',
            { uid: this.car.value.uid },
            (data) => {
                return data;
            },
            { showDefaultAlert: false }
        );
    }

    async routeToNextPicture() {
        if (this.editMode) {
            this.editMode = false;
            return;
        }
        const currentPosition = this.router.url.split('/').pop();
        const nextPosition = this.requiredImages.value[this.requiredImages.value.indexOf(currentPosition) + 1];
        const url = this.router.url.replace(currentPosition, nextPosition);

        if (nextPosition !== undefined) {
            this.navController.navigateForward(url);
        } else {
            if (this.editPicturesMode) {
                const loading = await this.loadingController.create({ message: 'Uploading Images...' });
                loading.present();
                await this.waitForImagesToUpload();
                this.navController.navigateBack('your-listings');
                loading.dismiss();
            } else {
                this.router.navigateByUrl(this.router.url.replace('/images/' + currentPosition, '/post-form'));
            }
        }
    }

    waitForImagesToUpload() {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (!this.isUploadingImages) {
                    resolve();
                    clearInterval(interval);
                }
            }, 200);
        });
    }

    routeToPreviousPicture() {
        if (this.editMode) {
            this.editMode = false;
            return;
        }
        const currentPosition = this.router.url.split('/').pop();
        const prevPosition = this.requiredImages.value[this.requiredImages.value.indexOf(currentPosition) + -1];
        const url = this.router.url.replace(currentPosition, prevPosition);

        if (prevPosition !== undefined) {
            this.navController.navigateBack(url);
        } else {
            const prevUrl = this.router.url;
            this.navController.pop().then(() => {
                const nextUrl = this.router.url;
                if (prevUrl === nextUrl) {
                    this.navController.navigateForward('seller-menu', { replaceUrl: true });
                }
            });
        }
    }

    navigateToImage(position: string, inEditMode: boolean = false) {
        if (this.editMode) {
            this.navController.pop();
            this.editMode = false;
        } else {
            this.editMode = inEditMode;
            this.navController.navigateForward(
                this.router.url.replace(this.router.url.split('/').pop(), 'images/' + position)
            );
        }
    }

    async navigateToFormPart(url: string, inEditMode: boolean = false) {
        if (this.editMode) {
            this.navController.pop();
            this.editMode = false;
        } else {
            this.editMode = inEditMode;

            let nextUrl;
            if (this.router.url.includes('images')) {
                nextUrl = this.isContinuedForm ? 'form/' + this.car.value.uid + '/' + url : 'form/new/' + url;
            } else {
                nextUrl = this.router.url.replace(this.router.url.split('/').pop(), url);
            }
            await this.router.navigateByUrl(nextUrl);
        }
        this.updateProgress();
    }

    vinIsDifferent(vin: string): boolean {
        return vin !== this.car.value.vin;
    }

    configureImageRoutes(currentConfig: Route, component: any) {
        currentConfig.children.pop();
        this.requiredImages.value.forEach((position) => {
            currentConfig.children.push({ path: position, component });
        });
    }

    isPictureTaken(position: string) {
        return this.pictures.value.get(position).taken;
    }

    get picturesValid(): boolean {
        const pics = this.pictures.value;

        for (let [key, value] of pics) {
            if (!value.taken) {
                return false;
            }
        }
        return true;
    }

    get waitForRequiredImagesLoad(): Observable<string[]> {
        return this.requiredImages.asObservable();
    }

    get isValueForm(): boolean {
        return this.router.url.includes('value');
    }

    isLastImage(position: string): boolean {
        return (
            this.requiredImages.value && this.requiredImages.value[this.requiredImages.value.length - 1] === position
        );
    }

    get requiredImagesValue(): string[] {
        return this.requiredImages.value;
    }
}

export interface AuctionSettings {
    start: string;
    end: string;
    length: number;
    reserve?: number;
    distance: number;
    buy_now: boolean;
    buy_now_price: number;
}

export interface PictureForm {
    image: any;
    uploaded: boolean;
    taken: boolean;
    error: boolean;
}
